'use client';

import { createPortal } from 'react-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './IdentityHeader.module.css';

/** Aire entre el borde de abajo de la línea y el globo. */
const GAP = 8;

/**
 * La línea de rol de la ficha, en UNA sola línea.
 *
 * El cargo llega tal como lo escribió RR.HH. y hay muchos que no entran en la
 * columna del perfil (340-380px, unos 47-53 caracteres). Partidos en dos dejaban
 * una palabra suelta abajo —"(CIC-UDA)" colgando— y la tarjeta se leía rota.
 *
 * El recorte lo hace CSS (`text-overflow: ellipsis`, en `.mainRole`), no JS, y esa
 * es la parte importante: el navegador lo mide EN PÍXELES al dibujar, así que
 * siempre muestra el máximo que entra en la caja real, en cualquier resolución y
 * sin recalcular nada al girar el teléfono. Y el texto completo QUEDA en el HTML
 * —recortar por CSS no borra nada—, así que buscadores y lectores de pantalla lo
 * siguen leyendo entero. Lo único que se pierde es lo que ve el ojo, y para eso
 * está el globo.
 *
 * No usa `useHoverTip` (el de los carriles) a propósito: esa etiqueta está hecha
 * para botones de icono y lo tiene cocinado —`white-space: nowrap` con tope de
 * 220px, y `display: none` bajo 1024px porque un toque dispararía la etiqueta de
 * cada item del menú—. Acá hace falta lo contrario: un texto largo que se acomode
 * en varias líneas, y que EN EL TELÉFONO funcione, porque tocar la línea es la
 * única forma de recuperar lo recortado cuando no hay mouse.
 */
export function RoleLine({ text }: { text: string }) {
	const ref = useRef<HTMLParagraphElement>(null);
	const [recortado, setRecortado] = useState(false);
	const [globo, setGlobo] = useState<{ top: number; left: number } | null>(null);

	// Sólo hay algo que mostrar si el texto NO entra, y eso depende del ancho real:
	// el mismo cargo entra en el escritorio y se recorta en el teléfono. Por eso se
	// mide con un observer y no una vez al montar.
	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		const medir = () => setRecortado(el.scrollWidth > el.clientWidth + 1);
		medir();
		const observer = new ResizeObserver(medir);
		observer.observe(el);
		return () => observer.disconnect();
	}, [text]);

	// Si deja de estar recortado con el globo abierto (se ensanchó la ventana), el
	// globo sobra: quedaría repitiendo un texto que ya se lee completo.
	if (globo && !recortado) setGlobo(null);

	const mostrar = useCallback(() => {
		const el = ref.current;
		if (!el || !recortado) return;
		const caja = el.getBoundingClientRect();
		setGlobo({ top: caja.bottom + GAP, left: caja.left + caja.width / 2 });
	}, [recortado]);

	const ocultar = useCallback(() => setGlobo(null), []);

	return (
		<p
			ref={ref}
			className={styles.mainRole}
			// Foco sólo cuando hay algo escondido: sumar una parada de tabulador a un
			// párrafo que se lee entero es ruido. Recortado, en cambio, es el único
			// camino para quien navega con teclado — y en el teléfono, tocarlo.
			tabIndex={recortado ? 0 : undefined}
			onMouseEnter={mostrar}
			onMouseLeave={ocultar}
			onFocus={mostrar}
			onBlur={ocultar}
		>
			{text}
			{globo && typeof document !== 'undefined'
				? createPortal(
						<span
							role="tooltip"
							className={styles.roleTip}
							style={{ top: globo.top, left: globo.left }}
						>
							{text}
						</span>,
						document.body,
					)
				: null}
		</p>
	);
}
